/* @ts-nocheck */
/* global google */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { GoogleMap, useLoadScript } from '@react-google-maps/api';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { PathLayer, LineLayer, ColumnLayer } from '@deck.gl/layers';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { RotateCwSquare, RotateCcwSquare, ArrowUpFromLine, ArrowDownFromLine } from 'lucide-react';
import ALRInfoPanel from './ALRInfoPanel';

const LIBRARIES = ['geometry'];

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const mapOptions = {
  disableDefaultUI: true,
  clickableIcons: false,
  mapTypeId: 'roadmap',
  mapId: '24ee09cb1783e3c39c8fcb02',
  minZoom: 10,
  maxZoom: 23,
  gestureHandling: 'greedy',
};

const parseGeometry = (geom) => {
  try {
    if (typeof geom === 'object' && geom?.type === 'LineString' && geom.coordinates) {
      return geom;
    }
    if (typeof geom === 'string') {
      if (geom.startsWith('LINESTRING')) {
        const coordsStr = geom.replace('LINESTRING(', '').replace(')', '');
        const coords = coordsStr.split(',').map(pair => {
          const [lng, lat] = pair.trim().split(' ').map(parseFloat);
          return [lng, lat];
        }).filter(coord => !isNaN(coord[0]) && !isNaN(coord[1]));
        return { type: 'LineString', coordinates: coords };
      } else if (geom.startsWith('{') || geom.startsWith('[')) {
        const parsed = JSON.parse(geom);
        if (parsed?.type === 'LineString' && parsed.coordinates) {
          return { type: 'LineString', coordinates: parsed.coordinates };
        }
      }
    }
    return null;
  } catch (err) {
    console.error('Error parsing geometry:', err);
    return null;
  }
};

const formatStation = (station) => {
  if (station === null || station === undefined) return 'N/A';
  const hundreds = Math.floor(station / 100);
  const remainder = station % 100;
  return `${hundreds}+${remainder.toFixed(1).padStart(4, '0')}`;
};

// Helper function to interpolate position along geometry based on station
const interpolatePositionAtStation = (geometry, fromStation, toStation, targetStation) => {
  if (!geometry || geometry.coordinates.length === 0) {
    return null;
  }
  
  const coords = geometry.coordinates;
  
  // If only one point, return it
  if (coords.length === 1) {
    return coords[0];
  }
  
  // Calculate the ratio of how far along the targetStation is
  const totalDistance = toStation - fromStation;
  if (totalDistance === 0) {
    return coords[0];
  }
  
  const ratio = (targetStation - fromStation) / totalDistance;
  const clampedRatio = Math.max(0, Math.min(1, ratio));
  
  // Interpolate between start and end coordinates
  const startCoord = coords[0];
  const endCoord = coords[coords.length - 1];
  
  const interpolatedLng = startCoord[0] + (endCoord[0] - startCoord[0]) * clampedRatio;
  const interpolatedLat = startCoord[1] + (endCoord[1] - startCoord[1]) * clampedRatio;
  
  return [interpolatedLng, interpolatedLat];
};

function ALRMapModal({ alrEvent, allALREvents, events = [], onClose }) {
  const [map, setMap] = useState(null);
  const [selectedALR, setSelectedALR] = useState(alrEvent);
  const [hoverTooltip, setHoverTooltip] = useState(null);
  const [hoveredEventId, setHoveredEventId] = useState(null); // Track which event is hovered
  const deckOverlayRef = useRef(null);
  const mapRef = useRef(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
    version: 'beta',
  });

  const geometry = useMemo(() => parseGeometry(selectedALR?.geom), [selectedALR]);

  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
    mapRef.current = mapInstance;
    if (geometry?.coordinates && geometry.coordinates.length > 0) {
      const coords = geometry.coordinates;
      const midIndex = Math.floor(coords.length / 2);
      const centerPoint = { lat: coords[midIndex][1], lng: coords[midIndex][0] };
      mapInstance.setCenter(centerPoint);
      mapInstance.setZoom(23);
      mapInstance.setTilt(45);
    }
  }, [geometry]);

  const adjustMap = useCallback((mode, amount) => {
    if (!mapRef.current) return;
    switch (mode) {
      case "tilt":
        mapRef.current.setTilt(Math.max(0, Math.min(67.5, mapRef.current.getTilt() + amount)));
        break;
      case "rotate":
        mapRef.current.setHeading((mapRef.current.getHeading() + amount + 360) % 360);
        break;
    }
  }, []);

  // Color function based on magnitude
  const getALRColor = useCallback((magnitude) => {
    if (magnitude > 250) return [244, 67, 54, 255];   // Red, full opacity
    if (magnitude > 200) return [255, 152, 0, 255];  // Orange, full opacity
    return [255, 193, 7, 255];                       // Amber, full opacity
  }, []);

  const alrLayers = useMemo(() => {
    if (!geometry?.coordinates) return [];
    
    const RAISED_ELEVATION_METERS = 3;
    const SHADOW_ELEVATION_METERS = 0.5;
    const BLACK_COLOR = [0, 0, 0, 255];
    const SHADOW_COLOR = [0, 0, 0, 120]; // Semi-transparent black for shadow
    const ALR_COLOR = getALRColor(selectedALR?.magnitude_in || 0);

    const pathRaised = geometry.coordinates.map(([lng, lat]) => [lng, lat, RAISED_ELEVATION_METERS]);
    const pathShadow = geometry.coordinates.map(([lng, lat]) => [lng, lat, SHADOW_ELEVATION_METERS]);

    return [
      // Shadow layer (same width as normal ALR)
      new PathLayer({
        id: 'alr-shadow',
        data: [1],
        getPath: () => pathShadow,
        getColor: SHADOW_COLOR,
        getWidth: 2,
        widthUnits: 'pixels',
        billboard: true,
        jointRounded: true,
        capRounded: true,
      }),
      
      // Main outline (raised)
      new PathLayer({
        id: 'alr-outline',
        data: [1],
        getPath: () => pathRaised,
        getColor: BLACK_COLOR,
        getWidth: 5,
        widthUnits: 'pixels',
        billboard: true,
        jointRounded: true,
        capRounded: true,
      }),
      
      // Main ALR line (raised, color based on magnitude)
      new PathLayer({
        id: 'alr-line',
        data: [1],
        getPath: () => pathRaised,
        getColor: ALR_COLOR,
        getWidth: 3,
        widthUnits: 'pixels',
        billboard: true,
        jointRounded: true,
        capRounded: true,
      }),
    ];
  }, [geometry, selectedALR, getALRColor]);

  // Layer to show all other ALRs (non-selected)
const allAlrLayers = useMemo(() => {
  if (!allALREvents || allALREvents.length === 0) return [];
  
  const ELEVATION_METERS = 0; // Ground level for non-selected
  const layers = [];
  
  allALREvents.forEach(alrEvent => {
    if (alrEvent.event_id === selectedALR?.event_id) return; // Skip selected one
    
    const geom = parseGeometry(alrEvent.geom);
    if (!geom?.coordinates) return;
    
    const pathElevated = geom.coordinates.map(([lng, lat]) => [lng, lat, ELEVATION_METERS]);
    const magnitude = alrEvent.magnitude_in || 0;
    const isHovered = hoveredEventId === alrEvent.event_id;
    
    layers.push(
      new PathLayer({
        id: `alr-other-${alrEvent.event_id}`,
        data: [{ ...alrEvent, alrData: true }],
        getPath: () => pathElevated,
        getColor: isHovered ? [255, 255, 255, 255] : getALRColor(magnitude),
        getWidth: isHovered ? 4 : 2,
        widthUnits: 'pixels',
        billboard: true,
        jointRounded: true,
        capRounded: true,
        pickable: true,
        autoHighlight: false,
        onHover: ({ object, x, y }) => {
          if (object) {
            setHoveredEventId(object.event_id);
            setHoverTooltip({
              x,
              y,
              content: {
                type: 'ALR',
                fromStation: formatStation(object.from_station_ft),
                toStation: formatStation(object.to_station_ft),
                magnitude: (object.magnitude_in || 0).toFixed(2),
                wheelpath: object.wheelpath || 'N/A',
              }
            });
          } else {
            setHoveredEventId(null);
            setHoverTooltip(null);
          }
        },
        onClick: ({ object }) => {
          if (object && object.alrData) {
            setSelectedALR(object);
            setHoveredEventId(null);
            setHoverTooltip(null);
          }
        },
      })
    );
  });
  
  return layers;
}, [allALREvents, selectedALR, getALRColor, hoveredEventId]);

  // Dip layers (pyramid/mound style spanning full length - inverted)
  const dipLayers = useMemo(() => {
    if (!selectedALR || !events || events.length === 0) return [];

    let visibleEvents = events.filter(e => {
      const type = (e.event_type || '').toUpperCase();
      return type === 'DIP';
    });

    const alrFromStation = selectedALR.from_station_ft;
    const alrToStation = selectedALR.to_station_ft;
    const alrWheelpath = selectedALR.wheelpath;

    visibleEvents = visibleEvents.filter(event => {
      const eventFromStation = event.from_station_ft;
      const eventToStation = event.to_station_ft;
      const eventWheelpath = event.wheelpath;

      const withinStationRange = 
        (eventFromStation >= alrFromStation && eventFromStation <= alrToStation) ||
        (eventToStation >= alrFromStation && eventToStation <= alrToStation) ||
        (eventFromStation <= alrFromStation && eventToStation >= alrToStation);

      const wheelpathMatches = !alrWheelpath || eventWheelpath === alrWheelpath;

      return withinStationRange && wheelpathMatches;
    });

    if (visibleEvents.length === 0) return [];

    const ALR_ELEVATION_METERS = 3;
    const ELEVATION_SCALE = 10;
    const NUM_LEVELS = 10;
    const NUM_SEGMENTS = 48;

    // Create data points for all segments of all events
    const segmentData = [];
    visibleEvents.forEach(event => {
      const fromStation = event.from_station_ft;
      const toStation = event.to_station_ft;
      const peakStation = event.peak_station_ft || fromStation;
      const magnitude = event.magnitude_in || 0;
      const geometry = parseGeometry(event.geom);
      
      if (!geometry) return;
      
      // Create segments along the length
      for (let i = 0; i < NUM_SEGMENTS; i++) {
        const segmentRatio = i / (NUM_SEGMENTS - 1);
        const currentStation = fromStation + (toStation - fromStation) * segmentRatio;
        
        const distanceToPeak = Math.abs(currentStation - peakStation);
        const totalLength = toStation - fromStation;
        
        const maxDistFromPeak = Math.max(peakStation - fromStation, toStation - peakStation);
        const heightRatio = maxDistFromPeak > 0 
          ? 1 - Math.pow(distanceToPeak / maxDistFromPeak, 2)
          : 1;
        
        const segmentHeight = magnitude * Math.max(0, heightRatio);
        
        const position = interpolatePositionAtStation(
          geometry,
          fromStation,
          toStation,
          currentStation
        );
        
        if (position) {
          segmentData.push({
            ...event,
            segmentIndex: i,
            position,
            segmentHeight,
            currentStation,
            isAtPeak: Math.abs(currentStation - peakStation) < (totalLength / NUM_SEGMENTS),
          });
        }
      }
    });

    const layers = [];

    // Create pyramid layers (inverted for dips - built from bottom up)
    for (let level = 0; level < NUM_LEVELS; level++) {
      const levelRatio = (level + 1) / NUM_LEVELS;
      
      layers.push(
        new ColumnLayer({
          id: `dip-pyramid-level-${level}`,
          data: segmentData,
          
          getPosition: d => {
            const [lng, lat] = d.position;
            
            const fullDepth = d.segmentHeight * ELEVATION_SCALE;
            const bottomZ = ALR_ELEVATION_METERS - fullDepth;
            const levelHeight = (fullDepth * level) / NUM_LEVELS;
            const baseZ = bottomZ + levelHeight;
            
            return [lng, lat, baseZ];
          },
          
          getElevation: d => (d.segmentHeight * ELEVATION_SCALE) / NUM_LEVELS,
          elevationScale: 1,

          getFillColor: d => {
            const baseAlpha = 255 - (level * 12);
            const isHovered = d.event_id === hoveredEventId;
            return isHovered 
              ? [255, 170, 50, Math.min(255, baseAlpha + 60)] 
              : [255, 140, 0, baseAlpha];
          },
          
          flatShading: false,
          coverage: 1.4,
          diskResolution: 32,
          radius: 0.18 * levelRatio,
          radiusUnits: 'meters',
          extruded: true,
          
          material: {
            ambient: 0.7,
            diffuse: 0.3,
            shininess: 8,
            specularColor: [15, 15, 15]
          },
          
          parameters: {
            depthTest: false,
          },
          
          visible: true,
          opacity: 1,
          pickable: true,
          autoHighlight: false,
          highlightColor: [255, 255, 255, 150],
          
          onHover: ({ object, x, y }) => {
            if (object) {
              setHoveredEventId(object.event_id);
              setHoverTooltip({
                x,
                y,
                content: {
                  type: 'Dip',
                  fromStation: formatStation(object.from_station_ft),
                  toStation: formatStation(object.to_station_ft),
                  peakStation: formatStation(object.peak_station_ft || object.from_station_ft),
                  magnitude: (object.magnitude_in || 0).toFixed(2),
                  wheelpath: object.wheelpath || 'N/A',
                }
              });
            } else {
              setHoveredEventId(null);
              setHoverTooltip(null);
            }
          },
        })
      );
    }

    return layers;
  }, [events, selectedALR, hoveredEventId]);

  // Bump layers (pyramid/mound style spanning full length - normal)
  const bumpLayers = useMemo(() => {
    if (!selectedALR || !events || events.length === 0) return [];

    let visibleEvents = events.filter(e => {
      const type = (e.event_type || '').toUpperCase();
      return type === 'BUMP';
    });

    const alrFromStation = selectedALR.from_station_ft;
    const alrToStation = selectedALR.to_station_ft;
    const alrWheelpath = selectedALR.wheelpath;

    visibleEvents = visibleEvents.filter(event => {
      const eventFromStation = event.from_station_ft;
      const eventToStation = event.to_station_ft;
      const eventWheelpath = event.wheelpath;

      const withinStationRange = 
        (eventFromStation >= alrFromStation && eventFromStation <= alrToStation) ||
        (eventToStation >= alrFromStation && eventToStation <= alrToStation) ||
        (eventFromStation <= alrFromStation && eventToStation >= alrToStation);

      const wheelpathMatches = !alrWheelpath || eventWheelpath === alrWheelpath;

      return withinStationRange && wheelpathMatches;
    });

    if (visibleEvents.length === 0) return [];

    const ALR_ELEVATION_METERS = 3;
    const ELEVATION_SCALE = 10;
    const NUM_LEVELS = 10;
    const NUM_SEGMENTS = 48;

    // Create data points for all segments of all events
    const segmentData = [];
    visibleEvents.forEach(event => {
      const fromStation = event.from_station_ft;
      const toStation = event.to_station_ft;
      const peakStation = event.peak_station_ft || fromStation;
      const magnitude = event.magnitude_in || 0;
      const geometry = parseGeometry(event.geom);
      
      if (!geometry) return;
      
      // Create segments along the length
      for (let i = 0; i < NUM_SEGMENTS; i++) {
        const segmentRatio = i / (NUM_SEGMENTS - 1);
        const currentStation = fromStation + (toStation - fromStation) * segmentRatio;
        
        const distanceToPeak = Math.abs(currentStation - peakStation);
        const totalLength = toStation - fromStation;
        
        const maxDistFromPeak = Math.max(peakStation - fromStation, toStation - peakStation);
        const heightRatio = maxDistFromPeak > 0 
          ? 1 - Math.pow(distanceToPeak / maxDistFromPeak, 2)
          : 1;
        
        const segmentHeight = magnitude * Math.max(0, heightRatio);
        
        const position = interpolatePositionAtStation(
          geometry,
          fromStation,
          toStation,
          currentStation
        );
        
        if (position) {
          segmentData.push({
            ...event,
            segmentIndex: i,
            position,
            segmentHeight,
            currentStation,
            isAtPeak: Math.abs(currentStation - peakStation) < (totalLength / NUM_SEGMENTS),
          });
        }
      }
    });

    const layers = [];

    // Create pyramid layers for bumps
    for (let level = 0; level < NUM_LEVELS; level++) {
      const levelRatio = (NUM_LEVELS - level) / NUM_LEVELS;
      
      layers.push(
        new ColumnLayer({
          id: `bump-pyramid-level-${level}`,
          data: segmentData,
          
          getPosition: d => {
            const [lng, lat] = d.position;
            
            const levelHeight = (d.segmentHeight * ELEVATION_SCALE * level) / NUM_LEVELS;
            const baseZ = ALR_ELEVATION_METERS + levelHeight;
            
            return [lng, lat, baseZ];
          },
          
          getElevation: d => d.segmentHeight / NUM_LEVELS,
          elevationScale: ELEVATION_SCALE,

          getFillColor: d => {
            const baseAlpha = 255 - (level * 12);
            const isHovered = d.event_id === hoveredEventId;
            return isHovered 
              ? [255, 80, 80, Math.min(255, baseAlpha + 60)] 
              : [255, 0, 0, baseAlpha];
          },
          
          flatShading: false,
          coverage: 1.4,
          diskResolution: 32,
          radius: 0.18 * levelRatio,
          radiusUnits: 'meters',
          extruded: true,
          
          material: {
            ambient: 0.7,
            diffuse: 0.3,
            shininess: 8,
            specularColor: [15, 15, 15]
          },
          
          parameters: {
            depthTest: false,
          },
          
          visible: true,
          opacity: 1,
          pickable: true,
          autoHighlight: false,
          highlightColor: [255, 255, 255, 150],
          
          onHover: ({ object, x, y }) => {
            if (object) {
              setHoveredEventId(object.event_id);
              setHoverTooltip({
                x,
                y,
                content: {
                  type: 'Bump',
                  fromStation: formatStation(object.from_station_ft),
                  toStation: formatStation(object.to_station_ft),
                  peakStation: formatStation(object.peak_station_ft || object.from_station_ft),
                  magnitude: (object.magnitude_in || 0).toFixed(2),
                  wheelpath: object.wheelpath || 'N/A',
                }
              });
            } else {
              setHoveredEventId(null);
              setHoverTooltip(null);
            }
          },
        })
      );
    }

    return layers;
  }, [events, selectedALR, hoveredEventId]);

  // Deck.gl overlay management
  useEffect(() => {
    if (!map) return;

    if (!deckOverlayRef.current) {
  const overlay = new GoogleMapsOverlay({ 
    layers: [...allAlrLayers, ...dipLayers, ...alrLayers, ...bumpLayers] 
  });
  overlay.setMap(map);
  deckOverlayRef.current = overlay;
}

    deckOverlayRef.current.setProps({ 
  layers: [...allAlrLayers, ...dipLayers, ...alrLayers, ...bumpLayers] 
});

  }, [map, alrLayers, dipLayers, bumpLayers, allAlrLayers]);


  useEffect(() => {
  if (map && selectedALR) {
    const geometry = parseGeometry(selectedALR.geom);
    if (geometry?.coordinates && geometry.coordinates.length > 0) {
      const coords = geometry.coordinates;
      const midIndex = Math.floor(coords.length / 2);
      const centerPoint = { lat: coords[midIndex][1], lng: coords[midIndex][0] };
      map.panTo(centerPoint);
      map.setZoom(23);
    }
  }
}, [selectedALR, map]);
  
  useEffect(() => {
    setSelectedALR(alrEvent);
    if (map && alrEvent) {
        const newGeometry = parseGeometry(alrEvent.geom);
        if (newGeometry?.coordinates && newGeometry.coordinates.length > 0) {
            const coords = newGeometry.coordinates;
            const midIndex = Math.floor(coords.length / 2);
            const centerPoint = { lat: coords[midIndex][1], lng: coords[midIndex][0] };
            map.panTo(centerPoint);
            map.setZoom(23);
        }
    }
  }, [alrEvent, map]);

  if (loadError) return <div className="fixed inset-0 bg-white flex items-center justify-center z-50">Error loading map.</div>;
  if (!isLoaded) return <div className="fixed inset-0 bg-white flex items-center justify-center z-50">Loading Map...</div>;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-7xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">ALR Location Viewer</h3>
          <button onClick={onClose} className="p-1 text-gray-500 rounded-full hover:bg-gray-100 hover:text-gray-800">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="flex-grow relative rounded-b-lg overflow-hidden">
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            options={mapOptions}
            onLoad={onLoad}
          />
          
          {/* Hover Tooltip */}
          {hoverTooltip && (
  <div
    className="fixed bg-gray-800 text-white p-3 rounded-lg shadow-xl text-sm z-[2000] pointer-events-none"
    style={{
      left: hoverTooltip.x + 10,
      top: hoverTooltip.y - 10,
      transform: 'translateY(-100%)'
    }}
  >
    <div className="font-semibold mb-2 flex items-center gap-2">
      <div className={`w-2 h-2 rounded-full ${
        hoverTooltip.content.type === 'Bump' ? 'bg-red-500' : 
        hoverTooltip.content.type === 'Dip' ? 'bg-orange-500' : 
        'bg-white'
      }`}></div>
      {hoverTooltip.content.type}
    </div>
    <div className="space-y-1 text-xs">
      <div className="flex justify-between gap-4">
        <span className="text-gray-300">From:</span>
        <span className="font-mono">{hoverTooltip.content.fromStation}</span>
      </div>
      <div className="flex justify-between gap-4">
        <span className="text-gray-300">To:</span>
        <span className="font-mono">{hoverTooltip.content.toStation}</span>
      </div>
      {hoverTooltip.content.peakStation && (
        <div className="flex justify-between gap-4">
          <span className="text-gray-300">Peak:</span>
          <span className="font-mono">{hoverTooltip.content.peakStation}</span>
        </div>
      )}
      <div className="flex justify-between gap-4">
        <span className="text-gray-300">Magnitude:</span>
        <span className={`font-mono font-semibold ${
          hoverTooltip.content.type === 'Bump' ? 'text-red-400' : 
          hoverTooltip.content.type === 'Dip' ? 'text-orange-400' : 
          'text-white'
        }`}>
          {hoverTooltip.content.magnitude} in
        </span>
      </div>
      <div className="flex justify-between gap-4">
        <span className="text-gray-300">Wheelpath:</span>
        <span className="font-mono">{hoverTooltip.content.wheelpath}</span>
      </div>
    </div>
    {hoverTooltip.content.type === 'ALR' && (
      <div className="mt-2 pt-2 border-t border-gray-600 text-xs text-gray-400">
        Click to select
      </div>
    )}
  </div>
)}
          
          {/* 3D Controls */}
          <div className="w-full max-w-xs absolute bottom-0 right-0 flex items-center gap-2 bg-white p-1.5 z-[1001]">
            <button
              onClick={() => adjustMap("rotate", 45)}
              className="p-2 rounded-md bg-white text-gray-600 hover:bg-gray-200 active:bg-gray-300 transition-all duration-100 transform active:scale-95"
              title="Rotate Left"
            >
              <RotateCcwSquare className="h-4 w-4" />
            </button>
            <button
              onClick={() => adjustMap("tilt", -15)}
              className="p-2 rounded-md bg-white text-gray-600 hover:bg-gray-200 active:bg-gray-300 transition-all duration-100 transform active:scale-95"
              title="Tilt Up"
            >
              <ArrowUpFromLine className="h-4 w-4" />
            </button>
            <button
              onClick={() => adjustMap("tilt", 15)}
              className="p-2 rounded-md bg-white text-gray-600 hover:bg-gray-200 active:bg-gray-300 transition-all duration-100 transform active:scale-95"
              title="Tilt Down"
            >
              <ArrowDownFromLine className="h-4 w-4" />
            </button>
            <button
              onClick={() => adjustMap("rotate", -45)}
              className="p-2 rounded-md bg-white text-gray-600 hover:bg-gray-200 active:bg-gray-300 transition-all duration-100 transform active:scale-95"
              title="Rotate Right"
            >
              <RotateCwSquare className="h-4 w-4" />
            </button>
          </div>

          {selectedALR && (
            <ALRInfoPanel
              event={selectedALR}
              onClose={onClose}
              allALREvents={allALREvents}
              onSelectALR={setSelectedALR}
              events={events}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default ALRMapModal;